import { useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import type { SchemaField } from "@motolynk/api-client";
import { MAX_MONEY, numberProblem, useRules } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Alert,
  Button,
  Checkbox,
  Icon,
  Label,
  NumberInput,
  PatternInput,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  textRules,
  useConfirm,
} from "@motolynk/ui";

import { combinations, newVariant, sameOptions, type EditorValues } from "./values";

type VariantErrors = Record<string, { message?: string } | undefined>;

/** The API's limits (backend apps/products/services/editor.py: SKU 64, MAX_PRICE, stock 0 to 10,000,000). */
const SKU_MAX = 64;
const MAX_STOCK = 10_000_000;
/** The SKU's characters; the API saves it in capitals, so it is typed in capitals. */
const SKU_TEXT = { ...textRules.sku, letterCase: "upper" } as const;

/** Variants (SKUs): one per combination of the chosen axis options, or a single SKU without axes. */
export function VariantsSection({ axes }: { axes: SchemaField[] }) {
  const { t } = useTranslation("products");
  const { control, register, setValue, formState } = useFormContext<EditorValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "variants", keyName: "key" });
  const variants = useWatch({ control, name: "variants" }) ?? [];
  const confirm = useConfirm();
  const rules = useRules();
  const priceCheck = rules.check.money({ min: 0.01, required: true });
  const optionalMoney = rules.check.money();
  const stockCheck = rules.check.whole({ max: MAX_STOCK, required: true });
  const codes = axes.map((axis) => axis.code);
  const [chosen, setChosen] = useState<Record<string, string[]>>(() => {
    const start: Record<string, string[]> = {};
    for (const code of codes) {
      start[code] = [
        ...new Set(variants.map((v) => v.options?.[code]).filter((v): v is string => Boolean(v))),
      ];
    }
    return start;
  });
  const errors = formState.errors.variants as
    (VariantErrors[] & { message?: string; root?: { message?: string } }) | undefined;
  const rootError = errors?.message ?? errors?.root?.message;

  const labelOf = (options: Record<string, string>) =>
    axes
      .map(
        (axis) => axis.options.find((option) => option.value === options[axis.code])?.label ?? "?",
      )
      .join(" / ");

  const addMissing = () => {
    // blank rows without options (e.g. the starting row of a new product) make way for the combinations
    const blank = variants
      .map((v, index) => ({ v, index }))
      .filter(
        ({ v }) => codes.some((code) => !v.options?.[code]) && !v.sku.trim() && !v.price.trim(),
      )
      .map(({ index }) => index);
    const kept = variants.filter((_, index) => !blank.includes(index));
    if (blank.length) remove(blank);
    const wanted = combinations(chosen, codes);
    const missing = wanted.filter(
      (combo) => !kept.some((v) => sameOptions(v.options ?? {}, combo, codes)),
    );
    missing.forEach((combo, index) => append(newVariant(combo, kept.length === 0 && index === 0)));
  };

  const setDefault = (index: number) =>
    variants.forEach((_, i) =>
      setValue(`variants.${i}.is_default`, i === index, { shouldDirty: true }),
    );

  const cellError = (index: number, field: string) =>
    (errors?.[index] as VariantErrors | undefined)?.[field]?.message;

  return (
    <div className="space-y-4">
      {rootError && <Alert tone="danger">{rootError}</Alert>}
      {axes.length > 0 ? (
        <div className="space-y-3 rounded-card border border-border p-4">
          <p className="text-sm text-muted-foreground">{t("editor.variants.chooseOptions")}</p>
          {axes.map((axis) => (
            <div key={axis.code} className="space-y-2">
              <p className="text-sm font-medium">{axis.label}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {axis.options.map((option) => {
                  const id = `axis-${axis.code}-${option.value}`;
                  const on = (chosen[axis.code] ?? []).includes(option.value);
                  return (
                    <div key={option.value} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={on}
                        onCheckedChange={(value) =>
                          setChosen((current) => ({
                            ...current,
                            [axis.code]:
                              value === true
                                ? [...(current[axis.code] ?? []), option.value]
                                : (current[axis.code] ?? []).filter((v) => v !== option.value),
                          }))
                        }
                      />
                      <Label htmlFor={id} className="font-normal">
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addMissing}>
            <Icon name="plus" size={14} />
            {t("editor.variants.generate")}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("editor.variants.single")}</p>
      )}
      {fields.length > 0 && (
        <div className="overflow-hidden rounded-card border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                {axes.length > 0 && <TableHead>{t("editor.variants.axes")}</TableHead>}
                <TableHead>{t("editor.variants.sku")}</TableHead>
                <TableHead>{t("editor.variants.price")}</TableHead>
                <TableHead>{t("editor.variants.mrp")}</TableHead>
                <TableHead>{t("editor.variants.cost")}</TableHead>
                <TableHead>{t("editor.variants.stock")}</TableHead>
                <TableHead>{t("editor.variants.active")}</TableHead>
                <TableHead>{t("editor.variants.default")}</TableHead>
                {axes.length > 0 && (
                  <TableHead className="sr-only">{t("editor.variants.remove")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((row, index) => {
                const name = axes.length ? labelOf(variants[index]?.options ?? {}) : "";
                const aria = (field: string) => `${field}${name ? ` (${name})` : ""}`;
                const optionsError = cellError(index, "options");
                return (
                  <TableRow key={row.key}>
                    {axes.length > 0 && (
                      <TableCell className="whitespace-nowrap font-medium">
                        {name}
                        {optionsError && <p className="text-xs text-danger">{optionsError}</p>}
                      </TableCell>
                    )}
                    {(["sku", "price", "mrp", "cost_price", "stock"] as const).map((field) => {
                      const error = cellError(index, field);
                      const label = t(`editor.variants.${field === "cost_price" ? "cost" : field}`);
                      return (
                        <TableCell
                          key={field}
                          className={field === "sku" ? "min-w-44 align-top" : "min-w-28 align-top"}
                        >
                          {field === "sku" ? (
                            <PatternInput
                              {...SKU_TEXT}
                              maxLength={SKU_MAX}
                              aria-label={aria(label)}
                              aria-invalid={Boolean(error)}
                              {...register(`variants.${index}.sku`, {
                                validate: rules.check.pattern("sku", true),
                              })}
                            />
                          ) : field === "stock" ? (
                            <NumberInput
                              {...register(`variants.${index}.stock`, { validate: stockCheck })}
                              min={0}
                              max={MAX_STOCK}
                              aria-label={aria(label)}
                              aria-invalid={Boolean(error)}
                            />
                          ) : (
                            <NumberInput
                              {...register(`variants.${index}.${field}`, {
                                validate:
                                  field === "price"
                                    ? priceCheck
                                    : field === "mrp"
                                      ? (value, all) => {
                                          const problem = optionalMoney(value);
                                          if (problem !== true) return problem;
                                          const price = all.variants[index]?.price ?? "";
                                          const fine =
                                            !value.trim() ||
                                            numberProblem(price, { decimals: 2 }) !== null ||
                                            !price.trim() ||
                                            Number(value) >= Number(price);
                                          return fine || t("editor.variants.mrpBelowPrice");
                                        }
                                      : optionalMoney,
                              })}
                              decimals={2}
                              unit="₹"
                              min={field === "price" ? 0.01 : 0}
                              max={MAX_MONEY}
                              aria-label={aria(label)}
                              aria-invalid={Boolean(error)}
                            />
                          )}
                          {error && <p className="mt-1 text-xs text-danger">{error}</p>}
                        </TableCell>
                      );
                    })}
                    <TableCell className="align-top">
                      <Switch
                        aria-label={aria(t("editor.variants.active"))}
                        checked={variants[index]?.is_active ?? true}
                        onCheckedChange={(on) =>
                          setValue(`variants.${index}.is_active`, on, { shouldDirty: true })
                        }
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <Checkbox
                        aria-label={aria(t("editor.variants.default"))}
                        checked={variants[index]?.is_default ?? false}
                        onCheckedChange={() => setDefault(index)}
                      />
                    </TableCell>
                    {axes.length > 0 && (
                      <TableCell className="align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={aria(t("editor.variants.remove"))}
                          onClick={() => {
                            const saved = variants[index];
                            if (!saved?.uid) {
                              remove(index); // a row added just now: nothing to lose
                              return;
                            }
                            confirm.ask({
                              title: t("editor.variants.confirmTitle", { sku: saved.sku || "—" }),
                              description: t("editor.variants.confirmHint"),
                              confirmLabel: t("editor.variants.remove"),
                              onConfirm: () => remove(index),
                            });
                          }}
                        >
                          <Icon name="trash" size={16} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {axes.length === 0 && fields.length === 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append(newVariant({}, true))}
        >
          <Icon name="plus" size={14} />
          {t("editor.variants.add")}
        </Button>
      )}
      {confirm.dialog}
    </div>
  );
}
